import { useApolloClient } from '@apollo/client';
import { Button, Row, Typography, Upload, type UploadProps, message, Space } from 'antd';
import { isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    ImportConfiguratorVariantPricesDocument,
    type ImportConfiguratorVariantPricesMutation,
    type ImportConfiguratorVariantPricesMutationVariables,
} from '../../../api/mutations/importConfiguratorVariantPrices';
import { CompanyTheme } from '../../../api/types';
import { ErrorContent } from '../../../components/importExport';
import CancelButton from '../../../components/modal/CancelButton';
import OkButton from '../../../components/modal/OkButton';
import { useThemeComponents } from '../../../themes';
import { allowedExtensions, validateExtensions } from '../../../utilities/extensions';

const StyledUpload = styled(Upload)`
    margin-left: 10px;
`;

const Container = styled(Space)`
    width: 100%;
    justify-content: center;
`;

export type ImportVariantPricesModalProps = {
    title: string;
    modelConfiguratorId: string;
    open: boolean;
    onDownloadClick: () => void;
    onClose: () => void;
};

const ImportVariantPricesModal = ({
    title,
    modelConfiguratorId,
    open,
    onDownloadClick,
    onClose,
}: ImportVariantPricesModalProps) => {
    const { t } = useTranslation(['modelConfiguratorDetails']);
    const apolloClient = useApolloClient();
    const [errors, setErrors] = useState<string>(null);
    const [updatedCount, setUpdatedCount] = useState<number>(null);
    const { theme, Modal } = useThemeComponents();

    const accept = useMemo(() => allowedExtensions.excel.join(','), []);

    const handleOnClose = useCallback(() => {
        onClose();
        setErrors(null);
        setUpdatedCount(null);
    }, [onClose]);

    const handleDownload = useCallback(
        e => {
            e.preventDefault();
            onDownloadClick();
        },
        [onDownloadClick]
    );

    const handleOnChange = useCallback(
        async ({ file }) => {
            if (file instanceof File) {
                const { data } = await apolloClient.mutate<
                    ImportConfiguratorVariantPricesMutation,
                    ImportConfiguratorVariantPricesMutationVariables
                >({
                    mutation: ImportConfiguratorVariantPricesDocument,
                    variables: {
                        modelConfiguratorId,
                        upload: file,
                    },
                });

                switch (data.result.__typename) {
                    case 'ImportInventorySuccess':
                        setUpdatedCount(data.result.updatedCount);
                        break;

                    case 'ImportInventoryFail':
                        setErrors(data.result.message);
                        break;

                    default:
                        throw new Error('Invalid response type');
                }
            }
        },
        [apolloClient, modelConfiguratorId]
    );

    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        file => {
            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [t]
    );

    const content = useMemo(() => {
        if (!isNil(errors)) {
            return <ErrorContent errors={errors} />;
        }

        if (!isNil(updatedCount)) {
            return (
                <Row align="middle" gutter={[14, 14]} justify="center">
                    <Typography>
                        {t('modelConfiguratorDetails:messages.importVariantPriceSuccess', { count: updatedCount })}
                    </Typography>
                </Row>
            );
        }

        return (
            <Row align="middle" gutter={[14, 14]} justify="center">
                <Typography>{t('modelConfiguratorDetails:modals.importVariantPrices.message')}</Typography>
                <Typography>{t('modelConfiguratorDetails:modals.importVariantPrices.note')}</Typography>
                <Container direction="horizontal">
                    <Button onClick={handleDownload} type="link">
                        {t('modelConfiguratorDetails:modals.importVariantPrices.buttons.downloadCurrentList')}
                    </Button>
                </Container>
            </Row>
        );
    }, [errors, updatedCount, t, handleDownload]);

    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="close" onClick={handleOnClose}>
                {t('modelConfiguratorDetails:modals.importVariantPrices.buttons.cancel')}
            </CancelButton>
        );

        if (!isNil(errors)) {
            return [cancelButton];
        }

        const okButton = (
            <StyledUpload
                key="upload"
                accept={accept}
                beforeUpload={beforeUpload}
                onChange={handleOnChange}
                showUploadList={false}
                style={{ marginLeft: '1rem' }}
            >
                <OkButton>{t('modelConfiguratorDetails:modals.importVariantPrices.buttons.upload')}</OkButton>
            </StyledUpload>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [accept, beforeUpload, errors, handleOnChange, handleOnClose, theme, t]);

    return (
        <Modal footer={footer} onCancel={handleOnClose} open={open} title={title} width={550}>
            {content}
        </Modal>
    );
};

export default ImportVariantPricesModal;

export type ImportVariantPricesProps = Pick<
    ImportVariantPricesModalProps,
    'title' | 'modelConfiguratorId' | 'onDownloadClick'
>;

export const useImportVariantPricesModal = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (props: ImportVariantPricesProps) => (
            <ImportVariantPricesModal onClose={actions.close} open={visible} {...props} />
        ),
    };
};
